import type { ToolDefinition, ToolType } from "../types";

const IMAGE_RESIZER_TOOL_TYPE = {
  label: "Free Image Resizer",
  icon: "Maximize",
} as const satisfies ToolType;

const imageResizerTool: ToolDefinition = {
  slug: "image-resizer",
  name: "Free Image Resizer Online: Change Photo Dimensions Instantly",
  title: "Free Online Image Resizer",
  description:
    "Resize images to exact pixel dimensions instantly. Change photo size without losing quality, adjust aspect ratios, and crop for social media or print. 100% free.",
  longDescription:
    "Quickly resize your images to exact pixel dimensions with our free online image resizer. Whether you need to adjust a photo for an Instagram post, convert a picture to 600x600 pixels for a visa application, or reduce file dimensions for a website, this tool processes everything right in your browser. Upload your file, set your custom width and height, and download the perfect size instantly.",
  toolType: {
    label: "Image Editor",
    icon: "Crop",
  },
  heroBadges: [
    "100% Free",
    "No Watermark",
    "No Account Required",
    "Maintains Quality",
    "Works on Mobile",
  ],
  category: "Editing Tools",
  categorySlug: "image-editing-tools",
  href: "/tools/image-resizer",
  api: "/api/v1/resize",
  keywords: [
    "snappy photo resizer software",
    "adjust image dimensions online",
    "convert jpeg to 600x600 pixels",
    "enlarge picture without losing quality",
    "fast aspect ratio changer app",
    "shrink webp file tool",
    "reduce logo size maker",
    "stretch photo height and width",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Resize an Image Online",
        description:
          "Follow these straightforward steps to change your image dimensions in seconds without downloading any complicated software.",
      },
      benefits: {
        title: "Why Use Our Free Photo Resizer?",
        description:
          "Adjust your picture size quickly, privately, and accurately without compromising on visual clarity.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Everything you need to know about resizing images, managing aspect ratios, and preserving photo quality across all formats.",
      },
    },
    intro: {
      title: "Change Image Dimensions Without Losing Quality",
      paragraphs: [
        "Finding the right image dimension editor shouldn't be complicated. Whether you are a social media manager needing to resize a picture to fit a Facebook profile, or a student trying to resize a photo to passport size, our tool is built to make the process effortless. By giving you direct control over width and height pixels, you can adjust image size online in mere seconds.",
        "One of the biggest challenges when you change image size online is maintaining the crispness of the original file. Our fast image resizer uses advanced in-browser rendering to ensure that when you shrink a photo size or reduce image dimensions, you don't introduce unnecessary blurring. You can seamlessly change PNG dimensions, shrink a WebP file, or convert JPEG to 600x600 pixels while keeping the output pristine.",
      ],
      note: "All processing happens securely. Your files are never stored or shared.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "Mastering Aspect Ratios and Custom Pixels",
        level: "h2",
        paragraphs: [
          "Understanding how to adjust the height and width of an image is crucial for modern digital tasks. When you upload a picture, our online photo resizing tool instantly detects its original dimensions and aspect ratio. This is your baseline. If you need an exact 500 x 500 image converter or an image resizer for 512x512 icons, you can simply type those numbers into the dimension fields.",
          "To avoid distortion, simply keep the 'Maintain Aspect Ratio' toggle checked. This makes the tool act as an intuitive image aspect ratio changer—when you type in a new width, it calculates the perfect height to match. If your goal is to stretch an image online to fit a highly specific, non-standard box (like a strict website banner), you can uncheck the aspect ratio lock and set both dimensions independently.",
        ],
        note: "Unlocking the aspect ratio allows for exact pixel targeting, but may stretch or squash the subject of your photo.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Popular Resizing Scenarios",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Social Media Uploads",
                icon: "Instagram",
                description:
                  "Easily convert an image to an Instagram post size, resize a photo for a Facebook profile pic, or prep a Twitter header.",
                accent: "primary",
              },
              {
                label: "Official Documents",
                icon: "FileText",
                description:
                  "Adjust a photo to passport size or convert an image to exactly 600x600 pixels for a US visa application.",
                accent: "primary",
              },
              {
                label: "Web Optimization",
                icon: "Laptop",
                description:
                  "Shrink an image file size to 50kb without losing quality online by downscaling the pixel dimensions for your blog.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Standard Social Media & Web Image Dimensions",
            description:
              "Use these reference pixels when entering custom dimensions in the resizer.",
            columns: [
              "Platform / Use Case",
              "Recommended Width (px)",
              "Recommended Height (px)",
              "Aspect Ratio",
            ],
            rows: [
              {
                "Platform / Use Case": "Instagram Square Post",
                "Recommended Width (px)": "1080",
                "Recommended Height (px)": "1080",
                "Aspect Ratio": "1:1",
              },
              {
                "Platform / Use Case": "Instagram Portrait",
                "Recommended Width (px)": "1080",
                "Recommended Height (px)": "1350",
                "Aspect Ratio": "4:5",
              },
              {
                "Platform / Use Case": "Facebook Cover Photo",
                "Recommended Width (px)": "820",
                "Recommended Height (px)": "312",
                "Aspect Ratio": "205:78",
              },
              {
                "Platform / Use Case": "YouTube Thumbnail",
                "Recommended Width (px)": "1280",
                "Recommended Height (px)": "720",
                "Aspect Ratio": "16:9",
              },
              {
                "Platform / Use Case": "Standard WhatsApp DP",
                "Recommended Width (px)": "500",
                "Recommended Height (px)": "500",
                "Aspect Ratio": "1:1",
              },
            ],
            note: "Select the 'Maintain Aspect Ratio' option if you want to scale down a larger image to fit these dimensions proportionally.",
            emphasisColumns: ["Platform / Use Case"],
            monospaceColumns: [
              "Recommended Width (px)",
              "Recommended Height (px)",
            ],
          },
        ],
      },
    ],
    referenceTables: [
      {
        title: "Common Exact-Match Resizing Requirements",
        description:
          "Specific document and profile picture requirements that require unchecking the aspect ratio lock if the source is not proportional.",
        columns: ["Requirement", "Target Dimensions", "Common Format"],
        rows: [
          {
            Requirement: "US Visa / DV Lottery",
            "Target Dimensions": "600 x 600 pixels",
            "Common Format": "JPEG",
          },
          {
            Requirement: "Standard Favicon",
            "Target Dimensions": "32 x 32 pixels",
            "Common Format": "PNG / ICO",
          },
          {
            Requirement: "App Icon",
            "Target Dimensions": "512 x 512 pixels",
            "Common Format": "PNG",
          },
          {
            Requirement: "Passport Photo (Digital)",
            "Target Dimensions": "Varies (often 600x600 or 2x2 inch equiv.)",
            "Common Format": "JPEG",
          },
        ],
        note: "When using the tool to convert a photo to 600x600 pixels, verify the official file size (KB) limits on the government portal before submitting.",
        emphasisColumns: ["Requirement"],
        monospaceColumns: ["Target Dimensions", "Common Format"],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your image",
        description:
          "Drag and drop your image onto the upload zone or click to browse. After upload, the tool shows your original dimensions, aspect ratio, and file size — so you know your starting point before setting target dimensions.",
      },
      {
        number: "02",
        title: "Choose a preset or enter custom dimensions",
        description:
          "Select a named preset from the dropdown (Instagram Square, YouTube Thumbnail, A4, and 17 others) to auto-fill the width and height fields. Or enter any custom pixel values directly for precise control.",
      },
      {
        number: "03",
        title: "Set aspect ratio preference",
        description:
          "Check Maintain Aspect Ratio to scale proportionally and avoid distortion — the tool adjusts height automatically when you change width. Uncheck it to set both dimensions independently for exact fixed output.",
      },
      {
        number: "04",
        title: "Resize and download",
        description:
          'Click "Resize Image" and watch the progress bar. The resized file is ready in seconds. Click "Download Resized Image" to save it to your device.',
      },
    ],
    benefits: [
      {
        icon: "Image",
        title: "Maintain Image Quality",
        description:
          "Resize image without losing quality online. Our intelligent scaling algorithms ensure your graphics and photos stay crisp, whether you shrink an image file size or enlarge photo dimensions slightly.",
      },
      {
        icon: "Lock",
        title: "Secure & Private Processing",
        description:
          "Your privacy matters. All image resizing happens securely right in your session, and we do not store or claim ownership of your uploaded files. No account required to start.",
      },
      {
        icon: "Maximize",
        title: "Custom & Preset Dimensions",
        description:
          "Whether you need to convert an image to 600x600 pixels for a visa or adjust a vertical photo for an Instagram post, just type the numbers or pick a convenient one-click preset.",
      },
      {
        icon: "FileCode2",
        title: "Perfect for All Formats",
        description:
          "Easily change PNG dimensions, shrink WebP file size, or reduce image size JPEG. We natively support all standard web formats and return your file in its original format.",
      },
      {
        icon: "Zap",
        title: "Lightning Fast Editing",
        description:
          "A truly fast image resizer. Drop your file, tweak the width and height, and click process. The new version is available to download instantly directly to your computer or mobile device.",
      },
    ],
    faqs: [
      {
        question:
          "How do I resize a photo for Instagram without losing quality?",
        answer:
          "To resize for an Instagram post without cropping awkwardly or degrading the image, select the 'Maintain Aspect Ratio' option if your image is already a 1:1 square or 4:5 portrait. Alternatively, use our built-in Instagram Square preset to automatically set the width and height to 1080x1080 pixels.",
      },
      {
        question:
          "How to convert a JPG to exactly 600x600 pixels for a US visa?",
        answer:
          "Upload your JPG file to the resizer. If your original image isn't perfectly square, uncheck the 'Maintain Aspect Ratio' toggle. Type '600' into both the width and the height fields, then click 'Resize Image'. Your picture will be correctly formatted for official online visa portals.",
      },
      {
        question:
          "Why do my photos look pixelated when I make them bigger online?",
        answer:
          "When you enlarge a picture or increase the dimension of an image online significantly beyond its original pixel count, the browser has to artificially fill in the missing pixels. This guessing process causes blurriness or pixelation. For the best visual results, it is always safer to shrink photo size rather than stretching a small image into a massive one.",
      },
      {
        question:
          "Is there a way to resize a logo for a website and keep the background transparent?",
        answer:
          "Absolutely. If you upload a transparent PNG or WebP file, our tool will automatically preserve that transparency channel. Just adjust the image dimensions to fit your website header or footer perfectly, and download the resized PNG.",
      },
      {
        question:
          "How can I change image dimensions on my phone without downloading an app?",
        answer:
          "Our online photo resizing tool is fully mobile-responsive. Just open the page in your iPhone or Android browser, tap the upload area to select your photo from your camera roll, enter your target dimensions, and tap download. No installation required.",
      },
      {
        question:
          "How do I fix the 'file too large' error when uploading a photo to a job site?",
        answer:
          "This error usually triggers because the pixel dimensions or the file size in KB/MB are too high for the site's server. You can fix this by reducing the photo dimensions (for example, downscaling to 1920x1080 or 1024x1024). Shrinking the dimensions will naturally reduce the final file size.",
      },
      {
        question:
          "How do I resize a picture for a WhatsApp DP without cropping?",
        answer:
          "A standard WhatsApp profile picture requires a 1:1 square ratio (typically around 500x500 pixels). If your image is wide or tall, you can use our tool to stretch the image online by unchecking 'Maintain Aspect Ratio' and forcing it into a 500x500 square. Note that doing this may visibly stretch or squash the subjects in the photo.",
      },
      {
        question: "What actually happens if I turn off the aspect ratio lock?",
        answer:
          "If you uncheck 'Maintain Aspect Ratio' and change the length and width of an image disproportionately, the image will physically stretch or compress to fit those exact pixel boundaries. This is highly useful when you absolutely must fit a strict dimension requirement, even if it slightly alters the image's natural proportions.",
      },
    ],
    useCases: [
      {
        label: "Social Media Managers",
        icon: "ThumbsUp",
        description:
          "Quickly resize a picture to fit a Facebook profile, format Twitter headers, or hit the exact Instagram DP size.",
        accent: "primary",
      },
      {
        label: "Web Developers & Bloggers",
        icon: "Code",
        description:
          "Resize WebP banners and change JPEG dimensions to fit strict container sizes and optimize layout shifts.",
        accent: "primary",
      },
      {
        label: "Visa & Passport Applicants",
        icon: "Plane",
        description:
          "Easily convert an image to 600x600 pixels or fulfill auto passport size photo requirements for digital forms.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Everyday Users",
        icon: "User",
        description:
          "Anyone who needs to quickly adjust image size online without navigating confusing professional editing software.",
        accent: "accent",
      },
      {
        label: "E-commerce Sellers",
        icon: "ShoppingCart",
        description:
          "Store owners needing to standardize product image dimensions to 1024 x 1024 for consistent catalog grids.",
        accent: "accent",
      },
    ],
  },
};

export default imageResizerTool;
export { imageResizerTool };
